import React, {ReactNode, useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Button, ButtonTypes} from "../../../react/components/ui/spectre/buttons";
import {Icon} from "../../../react/components/ui/spectre/icon";
import {Config, determineNextStep, StepAction, StepEvaluationWithVillage, VillageState as VillageStateType} from "../../Calculator/ResourceUpgradeOrder";
import {fillPartialFeaturesWithDefaults} from "../../Helper/TravianVersionHandling";
import TravianVersion from "../../enum/version";
import {areResourcesEqual, getNewResources, Resource, Resources, resourcesToArray} from "../../enum/Resource";
import {useTranslationContext} from "../../../react/providers/TranslationContext";
import WithTooltip from "../../../react/components/ui/withTooltip";
import Checkbox from "../../../react/components/ui/spectre/checkbox";
import ImageBuilding from "../../Components/ImageBuilding/ImageBuilding";
import {ImageOasisGeneric} from "../../Components/ImageMisc/ImageOasis";
import {VillageState} from "./VillageState";

import './ResourceUpgradeOrder.sass';
import {Dropdown} from "../../../react/components/ui/spectre/Dropdown";
import {TravianIcon} from "../../../react/components/ui/tIcon";
import ImageMisc from "../../Components/ImageMisc/ImageMisc";

const oasisTypes = [
    getNewResources(0, 0, 0, 50),
    getNewResources(0, 0, 0, 25),
    getNewResources(25, 0, 0, 25),
    getNewResources(0, 25, 0, 25),
    getNewResources(0, 0, 25, 25),
    getNewResources(50, 0, 0, 0),
    getNewResources(25, 0, 0, 0),
    getNewResources(0, 50, 0, 0),
    getNewResources(0, 25, 0, 0),
    getNewResources(0, 0, 50, 0),
    getNewResources(0, 0, 25, 0),
];

const availableFieldTypes = [
    getNewResources(0, 0, 0, 18),
    getNewResources(1, 1, 1, 15),
    getNewResources(3, 3, 3, 9),
    getNewResources(4, 4, 4, 6),
    getNewResources(3, 4, 4, 7),
    getNewResources(4, 3, 4, 7),
    getNewResources(4, 4, 3, 7),
    getNewResources(3, 5, 4, 6),
    getNewResources(3, 4, 5, 6),
    getNewResources(5, 3, 4, 6),
    getNewResources(4, 3, 5, 6),
    getNewResources(5, 4, 3, 6),
    getNewResources(4, 5, 3, 6),
];

function parseAndClampValue(levelString: string, min: number, max: number): number {
    const value = Math.min(max, Math.max(min, Math.round(Number(levelString))));
    return isNaN(value) ? 0 : value;
}

export default function(): ReactNode {
    const { translate: _translate, getTranslator } = useTranslationContext();
    const translate = getTranslator('tool/trav/ResourceUpgradeOrder');

    const [fieldTypes, setFieldTypes] = useState<Resources>(getNewResources(1, 1, 1, 15));
    const [initialLevels, setInitialLevels] = useState<Array<number>>([3, 3, 4, ...Array(9).fill(7), ...Array(6).fill(6)]);
    const [boosterLevels, setBoosterLevels] = useState<Array<number>>([0, 0, 0, 0, 0]);
    const [waterworksLevel, setWaterworksLevel] = useState<number>(0);
    const [oasisBuildingLevel, setOasisBuildingLevel] = useState<number>(0);
    const [availableOases, setAvailableOases] = useState<Array<number|null>>([null, null, null]);
    const [oasesAnnexation, setOasesAnnexation] = useState<Array<boolean>>([false, false, false]);
    const [externalProduction, setExternalProduction] = useState<Resources>(getNewResources());

    const [enableGoldProduction, setEnableGoldProduction] = useState<boolean>(true);
    const [enableWaterworks, setEnableWaterworks] = useState<boolean>(true);
    const [enableWCIBoosters, setEnableWCIBoosters] = useState<boolean>(false);
    const [enableWCIFields, setEnableWCIFields] = useState<boolean>(true);

    const [villageCapital, setVillageCapital] = useState<boolean>(true);
    const [villageCity, setVillageCity] = useState<boolean>(false);

    const [searchParams, setSearchParams] = useSearchParams();

    const [lastVillageState, setLastVillageState] = useState<VillageStateType|null>(null);
    const [aggregatedSteps, setAggregatedSteps] = useState<Array<{steps: Array<StepEvaluationWithVillage> }>>([]);

    const [needRecalculation, setNeedRecalculation] = useState<boolean>(true);

    const config: Config = {
        travianFeatures: fillPartialFeaturesWithDefaults({
            features: {
                speed: 5,
            },
            version: TravianVersion.Legends,
        }),
        availableOases: availableOases
            .filter((oasis: number|null) => oasis !== null)
            .map(index => oasisTypes[index]),
        initialVillageState: {
            resourceTypes: [
                ...Array(fieldTypes[Resource.Wood]).fill(Resource.Wood),
                ...Array(fieldTypes[Resource.Clay]).fill(Resource.Clay),
                ...Array(fieldTypes[Resource.Iron]).fill(Resource.Iron),
                ...Array(fieldTypes[Resource.Crop]).fill(Resource.Crop),
            ],
            resourceLevel: initialLevels,
            waterworks: waterworksLevel,
            oasisBuilding: oasisBuildingLevel,
            boosterLevel: boosterLevels,
            oases: availableOases
                .filter((oasis: number|null, index) => oasis !== null && oasesAnnexation[index])
                .map(index => oasisTypes[index]),
        },
        maxFieldLevel: villageCapital ? 25 : (villageCity ? 12 : 10),
        externalProduction,
        enableGoldProduction,
        enableWaterworks,
        enableWCIBoosters,
        enableWCIFields,
    }

    const oasisBuildingGid = config.travianFeatures.version === TravianVersion.Legends ? 37 : 18;

    // Parse URL parameters once
    useEffect(() => {
        // If people want to have fun experimenting with non-18-field-villages, be my guest.
        let resourceFieldCount = initialLevels.length;

        const type = searchParams.get('fields') ?? '';
        const types = type.split('-').map(amount => parseInt(amount));
        if (types.length === 4 && types.every(type => type >= 0 && !isNaN(type))) {
            setFieldTypes(getNewResources(...types));
            resourceFieldCount = types.reduce((a, b) => a + b, 0);
        }

        const fields = searchParams.get('levels') ?? '';
        const fieldLevels = fields.split('-').map(level => Math.round(Number(level)));
        if (fieldLevels && fieldLevels.length === resourceFieldCount && fieldLevels.every(level => !isNaN(level) && 0 <= level && level <= 25)) {
            setInitialLevels(fieldLevels);
        } else {
            setInitialLevels(Array(resourceFieldCount).fill(0));
        }

        const boosters = searchParams.get('boosters') ?? '';
        const boosterLevels = boosters.split('-').map(level => Math.round(Number(level)));
        if (boosters && boosterLevels.length === 5 && boosterLevels.every(level => !isNaN(level) && 0 <= level && level <= 5)) {
            setBoosterLevels(boosterLevels);
        }

        const ww = Math.round(Number(searchParams.get('ww') ?? ''));
        if (!isNaN(ww) && 0 <= ww && ww <= 20) {
            setWaterworksLevel(ww);
        }

        const ob = Math.round(Number(searchParams.get('ob') ?? ''));
        if (!isNaN(ob) && 0 <= ob && ob <= 20) {
            setOasisBuildingLevel(ob);
        }

        const b = parseInt(searchParams.get('b') ?? '', 16);
        if (!isNaN(b) && b >= 0) {
            const booleanSettings = Array(9).fill(null).map((_, index) => !!((b >> index) & 1)).reverse();
            const [
                enableGoldProduction,
                enableWaterworks,
                enableWCIBoosters,
                enableWCIFields,
                oasis1,
                oasis2,
                oasis3,
                villageCapital,
                villageCity,
            ] = booleanSettings;
            setEnableWCIFields(enableWCIFields);
            setEnableWCIBoosters(enableWCIBoosters);
            setEnableWaterworks(enableWaterworks);
            setEnableGoldProduction(enableGoldProduction);
            setOasesAnnexation([oasis1, oasis2, oasis3]);
            setVillageCapital(villageCapital);
            setVillageCity(villageCity);
        }

        const ao = searchParams.get('ao') ?? '';
        const availableOases = ao.split('-').map(oasis => oasis === 'n' ? null : parseInt(oasis, 16));
        if (availableOases.length === 3 && availableOases.every(oasis => (!isNaN(oasis) && oasisTypes[oasis]) || oasis === null)) {
            setAvailableOases(availableOases);
        }

        const xp = searchParams.get('xp') ?? '';
        const externalProduction = xp.split('x').map(value => Math.round(Number(value)));
        if (xp && externalProduction.length === 4 && boosterLevels.every(value => !isNaN(value))) {
            setExternalProduction(getNewResources(...externalProduction));
        }
    }, [])

    // Serialize initial village state into URL, to make it shareable
    useEffect(() => {
        const fieldTypeAsResource = config.initialVillageState.resourceTypes.reduce((a, b) => {
            a[b]++;
            return a;
        }, getNewResources());
        const fieldTypeString = [
            fieldTypeAsResource[Resource.Wood],
            fieldTypeAsResource[Resource.Clay],
            fieldTypeAsResource[Resource.Iron],
            fieldTypeAsResource[Resource.Crop],
        ].join('-');
        const booleanSettings = [
            enableGoldProduction,
            enableWaterworks,
            enableWCIBoosters,
            enableWCIFields,
            oasesAnnexation[0],
            oasesAnnexation[1],
            oasesAnnexation[2],
            villageCapital,
            villageCity,
        ];
        const params = {
            fields: fieldTypeString,
            levels: initialLevels.join('-'),
            boosters: boosterLevels.join('-'),
            ww: waterworksLevel.toString(),
            ob: oasisBuildingLevel.toString(),
            b: booleanSettings.reduce((a, b) => (a << 1) | (b ? 1 : 0), 0).toString(16),
            ao: availableOases.map(oasis => oasis === null ? 'n' : oasis.toString(16)).join('-'),
            xp: resourcesToArray(externalProduction).join('x'),
        }
        setSearchParams(new URLSearchParams(params));
        setNeedRecalculation(true);
    }, [
        fieldTypes,
        initialLevels,
        boosterLevels,
        waterworksLevel,
        oasisBuildingLevel,
        enableWaterworks,
        enableGoldProduction,
        enableWCIBoosters,
        enableWCIFields,
        oasesAnnexation,
        availableOases,
        villageCapital,
        villageCity,
        externalProduction,
    ]);

    // Disable Waterworks for Kingdoms
    useEffect(() => {
        if (enableWaterworks && config.travianFeatures.version === TravianVersion.Kingdoms) {
            setEnableWaterworks(false);
        }
    }, [config.travianFeatures.version])

    function recalculate() {
        calculateAggregatedSteps(true);
    }

    function calculateMore() {
        calculateAggregatedSteps(needRecalculation);
    }

    function calculateAll() {
        calculateAggregatedSteps(needRecalculation, true)
    }

    function calculateAggregatedSteps(fullRecalculation: boolean = false, unlimited: boolean = false) {
        const limit = unlimited ? 1000 : 30;

        // If we have last village state, we calculate [limit] steps from there, and not from start.
        let village = (fullRecalculation || !lastVillageState) ? config.initialVillageState : lastVillageState;

        const steps: Array<StepEvaluationWithVillage> = fullRecalculation
            ? []
            : aggregatedSteps.map(aStep => aStep.steps).flat(1);

        for (let i = 0; i < limit; i++) {
            const stepData = determineNextStep(config, village);
            if (stepData === null) {
                // Out of possible steps
                break;
            }
            const { evaluation, village: newVillage } = stepData;
            steps.push({ village: newVillage, previousVillage: village, step: evaluation });
            village = newVillage;
        }

        const newAggregatedSteps: Array<{ steps: Array<StepEvaluationWithVillage> }> = [];
        let startCurrentAggregation = 0;
        for (let i = 1; i < steps.length; i++) {
            const step = steps[i].step.step;
            const lastStep = steps[i - 1].step.step;
            const village = steps[i].village;

            let mergeable = false;
            if (step.action === lastStep.action) {
                switch (step.action) {
                    case StepAction.upgradeWaterworks:
                    case StepAction.upgradeOasisBuilding:
                        mergeable = true;
                        break;
                    case StepAction.upgradeBooster:
                        // Same booster => mergeable
                        // @ts-ignore It's typesafe, but TS doesn't type-check _that_ smartly.
                        mergeable = step.upgradeIndex === lastStep.upgradeIndex;
                        break;
                    case StepAction.upgradeField:
                        // Same resource field type and same target level => mergeable
                        // @ts-ignore It's typesafe, but TS doesn't type-check _that_ smartly.
                        mergeable = step.upgradeTo === lastStep.upgradeTo && village.resourceTypes[step.upgradeIndex] === village.resourceTypes[lastStep.upgradeIndex];
                        break;
                    case StepAction.annectOasis:
                        mergeable = false;
                        break;
                }
            }

            if (!mergeable || i === steps.length - 1) {
                const lastIncludedStep = (i === steps.length - 1 ? steps.length - 1 : i - 1);
                newAggregatedSteps.push({
                    steps: steps.slice(startCurrentAggregation, lastIncludedStep + 1),
                });
                startCurrentAggregation = lastIncludedStep + 1;
            }
        }

        setLastVillageState(village);
        setAggregatedSteps(newAggregatedSteps);
        setNeedRecalculation(false);
    }

    function makeFieldInputs(resource: Resource) {
        function onChange(index: string, newValue: string) {
            const level = parseAndClampValue(newValue, 0, 25);
            const levels = [...initialLevels];
            for (let i = Number(index); i < levels.length; i++) {
                if (config.initialVillageState.resourceTypes[i] === resource) {
                    levels[i] = level;
                }
            }
            setInitialLevels(levels);
        }

        const ofType = Object.entries(config.initialVillageState.resourceLevel)
            .filter(([index, _]) => config.initialVillageState.resourceTypes[index] === resource);

        return ofType.map(([fieldIndex, fieldLevel]) =>
            <input
                type='number'
                min={0}
                max={25}
                step={1}
                className='smallNumberInput'
                style={{ width: '26px' }}
                key={fieldIndex}
                onChange={(e) => onChange(fieldIndex, e.target.value)}
                value={fieldLevel}
            />
        );
    }

    function makeBoosterInput(boosterIndex: number) {
        const boosterLevel = boosterLevels[boosterIndex];

        function onChange(newValue: string) {
            const level = parseAndClampValue(newValue, 0, 5);
            const newBoosters = [...boosterLevels];
            newBoosters[boosterIndex] = level;
            setBoosterLevels(newBoosters);
        }

        return <input
            type='number'
            min={0}
            max={5}
            step={1}
            className='smallNumberInput'
            style={{width: '26px'}}
            onChange={(e) => onChange(e.target.value)}
            value={boosterLevel}
        />
    }

    function setAvailableOasis(index: 0|1|2, oasisType: number|null): void {
        const oases = [...availableOases];
        oases[index] = oasisType;
        setAvailableOases(oases);
    }

    function setOasisAnnexed(index: 0|1|2, annexed: boolean): void {
        const annexationStatus = [...oasesAnnexation];
        annexationStatus[index] = annexed;
        setOasesAnnexation(annexationStatus);
    }

    let currentlySelectedFieldIndex = availableFieldTypes.findIndex(types => areResourcesEqual(types, fieldTypes));
    if (currentlySelectedFieldIndex === -1) {
        // We could have some weird custom stuff, let's just semi-permanently add it if we don't find it.
        availableFieldTypes.unshift(fieldTypes);
        currentlySelectedFieldIndex = 0;
    }

    const dropdownOptions = Object.fromEntries(availableFieldTypes.map((type, typeIndex) => {
        return [
            typeIndex.toString(),
            <div className='resourceFieldDistribution' key={JSON.stringify(type)}>
                <ImageMisc type='wood' size={32} /> {type[Resource.Wood]}
                <ImageMisc type='clay' size={32} /> {type[Resource.Clay]}
                <ImageMisc type='iron' size={32} /> {type[Resource.Iron]}
                <ImageMisc type='crop' size={32} /> {type[Resource.Crop]}
            </div>,
        ]
    }));

    function changeResourceFields(newIndex: string): void {
        setFieldTypes(availableFieldTypes[newIndex]);
        const newResourceTiles = Object.values(availableFieldTypes[newIndex] as Resources).reduce((a, b) => a + b, 0)
        if (newResourceTiles !== initialLevels.length) {
            setInitialLevels([
                // Take old ones, but cut down
                ...initialLevels.slice(0, newResourceTiles),
                // and fill up with 0s
                ...Array(Math.max(0, newResourceTiles - initialLevels.length)).fill(0),
            ])
        }
    }

    function makeExternalProductionInput(resource: Resource): ReactNode {
        function onChange(newValue: string): void {
            const value = parseInt(newValue);
            if (isNaN(value) || value === externalProduction[resource]) {
                return;
            }
            const newProduction = {...externalProduction};
            newProduction[resource] = value;
            setExternalProduction(newProduction);
        }

        return <input
            type='text'
            className='smallNumberInput'
            onChange={(e) => onChange(e.target.value)}
            value={externalProduction[resource]}
        />
    }

    return <div className="upgradeOrderListContainer">
        <div className='configurationContainer'>
            <div className='settingsContainer'>
                <div className='leftSettings'>
                    <h4>{translate('headerSettings')}</h4>

                    <div>
                        <Dropdown searchable={false} buttonTypes={[]} value={currentlySelectedFieldIndex.toString()} onChange={changeResourceFields} options={dropdownOptions} />
                    </div>

                    <div>
                        <Checkbox active={villageCapital} setActive={setVillageCapital} label={translate('villageCapital')} />
                    </div>
                    <div className={villageCapital ? 'text-gray' : ''}>
                        <Checkbox active={villageCity} setActive={setVillageCity} label={translate('villageCity')} />
                    </div>
                    <div>
                        <Checkbox active={enableGoldProduction} setActive={setEnableGoldProduction} label={translate('enableGoldProduction')} />
                    </div>
                    <div>
                        <Checkbox active={enableWaterworks} setActive={setEnableWaterworks} label={translate('enableWaterworks')} />
                    </div>
                    <div>
                        <Checkbox active={enableWCIFields} setActive={setEnableWCIFields} label={translate('enableWCIFields')} />
                    </div>
                    <div>
                        <Checkbox active={enableWCIBoosters} setActive={setEnableWCIBoosters} label={translate('enableWCIBoosters')} />
                    </div>

                    <Button onClick={recalculate}><Icon type='refresh' /> {translate('recalculate')}</Button>
                </div>

                <div className='rightSettings'>
                    <div>
                        <h4>{translate('headerOases')}</h4>

                        {[0, 1, 2].map((oasisIndex: 0 | 1 | 2) =>
                            <div className='oasisSelector' key={oasisIndex}>
                                <div>
                                    <Checkbox
                                        active={oasesAnnexation[oasisIndex] ?? false}
                                        setActive={(annexed: boolean) => setOasisAnnexed(oasisIndex, annexed)}
                                        label={translate('oasisAnnexed')}
                                    />
                                </div>

                                <div>
                                    <Button
                                        key='none'
                                        className='oasisSelectionButton'
                                        buttonTypes={availableOases[oasisIndex] === null ? [ButtonTypes.primary] : []}
                                        onClick={() => setAvailableOasis(oasisIndex, null)}
                                    >
                                        -
                                    </Button>

                                    {oasisTypes.map((oasis, typeIndex) =>
                                        <Button
                                            key={typeIndex}
                                            className='oasisSelectionButton'
                                            buttonTypes={availableOases[oasisIndex] === typeIndex ? [ButtonTypes.primary] : []}
                                            onClick={() => setAvailableOasis(oasisIndex, typeIndex)}
                                        >
                                            <ImageOasisGeneric size={32} oasis={oasis} />
                                        </Button>)}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <h4>{translate('headerExternalProduction')}</h4>

                        {translate('infoExternalProduction')}

                        <div className='productionInput'>
                            <ImageMisc type='wood' size={32} />
                            {makeExternalProductionInput(Resource.Wood)}

                            <ImageMisc type='clay' size={32} />
                            {makeExternalProductionInput(Resource.Clay)}

                            <ImageMisc type='iron' size={32} />
                            {makeExternalProductionInput(Resource.Iron)}

                            <ImageMisc type='crop' size={32} />
                            {makeExternalProductionInput(Resource.Crop)}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div className='upgradeOrderList'>
            <div className='villageStateWrapper'>
                <div></div>
                <div></div>
                <div></div>
                <div><ImageBuilding gid={1} /></div>
                <div><ImageBuilding gid={2} /></div>
                <div><ImageBuilding gid={3} /></div>
                <div><ImageBuilding gid={4} /></div>
                <div><ImageBuilding gid={5} grayscale={!config.enableWCIBoosters} /></div>
                <div><ImageBuilding gid={6} grayscale={!config.enableWCIBoosters} /></div>
                <div><ImageBuilding gid={7} grayscale={!config.enableWCIBoosters} /></div>
                <div><ImageBuilding gid={8} /></div>
                <div><ImageBuilding gid={9} /></div>
                <div><WithTooltip tooltip={_translate('travian/building_' + oasisBuildingGid)}><ImageBuilding gid={oasisBuildingGid} /></WithTooltip></div>
                <div><WithTooltip tooltip={translate('waterworks')}><ImageBuilding gid={45} grayscale={!config.enableWaterworks} /></WithTooltip></div>
                <div>{translate('oases')}</div>
                <div>{translate('productionAfter')}</div>
                <div>{translate('costTime')}</div>
                <div>{translate('infoROI')}</div>
            </div>

            <div className='villageStateWrapper'>
                <div></div>
                <div></div>
                <div></div>
                <div>{makeFieldInputs(Resource.Wood)}</div>
                <div>{makeFieldInputs(Resource.Clay)}</div>
                <div>{makeFieldInputs(Resource.Iron)}</div>
                <div>{makeFieldInputs(Resource.Crop)}</div>
                <div>{makeBoosterInput(0)}</div>
                <div>{makeBoosterInput(1)}</div>
                <div>{makeBoosterInput(2)}</div>
                <div>{makeBoosterInput(3)}</div>
                <div>{makeBoosterInput(4)}</div>
                <div>
                    <input
                        type='number'
                        min={0}
                        max={20}
                        step={1}
                        className='smallNumberInput'
                        style={{width: '26px'}}
                        onChange={(e) => setOasisBuildingLevel(parseAndClampValue(e.target.value, 0, 20))}
                        value={oasisBuildingLevel}
                    />
                </div>
                <div>
                    <input
                        type='number'
                        min={0}
                        max={20}
                        step={1}
                        className='smallNumberInput'
                        style={{width: '26px'}}
                        onChange={(e) => setWaterworksLevel(parseAndClampValue(e.target.value, 0, 20))}
                        value={waterworksLevel}
                    />
                </div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
            </div>

            <VillageState key={'start'} config={config} lastSteps={[]} village={config.initialVillageState} />
            {aggregatedSteps.map(({ steps }, index) =>
                <VillageState key={index} config={config} lastSteps={steps} stepNumber={(index + 1) + '.'} />)}
        </div>

        <div className='mt-2 text-center'>
            <Button onClick={calculateMore}>{translate('calculateMore')}</Button>
            <Button onClick={calculateAll}>{translate('calculateAll')}</Button>
        </div>
    </div>
}
